/**
 * Note card component for displaying individual notes with actions
 */

import React, { useState, useRef, useEffect } from 'react';
import { ReadingNote } from '../../types/reading-notes';
import NoteImageGallery from './NoteImageGallery';
import { MarkdownRenderer } from '../MarkdownRenderer';
import {
  TrashIcon,
  PencilIcon,
  ClipboardDocumentIcon,
  BookOpenIcon,
  ArrowPathIcon,
  PlayCircleIcon
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../contexts/ToastContext';

interface NoteCardProps {
  note: ReadingNote;
  bookId: string;
  onDelete: (noteId: string) => Promise<void>;
  onUpdate: (noteId: string, updates: Partial<ReadingNote>) => Promise<void>;
  onReindex?: (noteId: string) => Promise<void>;
  searchQuery?: string;
  isHighlighted?: boolean;
}

export default function NoteCard({ note, bookId, onDelete, onUpdate, onReindex, searchQuery = '', isHighlighted = false }: NoteCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedNote, setEditedNote] = useState(note.user_note || '');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isReindexing, setIsReindexing] = useState(false);
  const editContainerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { showToast } = useToast();

  // Auto-scroll to edit container when editing starts
  useEffect(() => {
    if (isEditing && editContainerRef.current) {
      editContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [isEditing]);

  // Get note type label
  const getNoteTypeLabel = () => {
    switch (note.note_type) {
      case 'highlight':
        return 'Highlight';
      case 'thought':
        return 'Thought';
      case 'chat_answer':
        return 'AI Answer';
    }
  };

  const noteTypeLabel = getNoteTypeLabel();

  // Get indexing status dot indicator (matching Dhyana style)
  const getIndexingStatusDot = () => {
    if (note.is_indexed) {
      return <div className="w-2 h-2 rounded-full bg-green-500" title="Indexed" />;
    }
    if (note.indexing_status === 'running' || note.indexing_status === 'pending') {
      return <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" title="Indexing..." />;
    }
    if (note.indexing_status === 'failed') {
      return <div className="w-2 h-2 rounded-full bg-red-500" title="Indexing failed" />;
    }
    return null;
  };

  // Handle copy to clipboard
  const handleCopy = async () => {
    const text = [
      note.content_text,
      note.user_note
    ].filter(Boolean).join('\n\n');

    try {
      await navigator.clipboard.writeText(text);
      showToast('success', 'Copied to clipboard');
    } catch (err) {
      showToast('error', 'Failed to copy');
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this note?')) {
      return;
    }

    setIsDeleting(true);
    try {
      await onDelete(note.id);
      showToast('success', 'Note deleted');
    } catch (err: any) {
      showToast('error', err.message || 'Failed to delete note');
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle save edit
  const handleSaveEdit = async () => {
    try {
      await onUpdate(note.id, { user_note: editedNote });
      setIsEditing(false);
      showToast('success', 'Note updated');
    } catch (err: any) {
      showToast('error', err.message || 'Failed to update note');
    }
  };

  // Handle jump to page
  const handleJumpToPage = () => {
    navigate(`/dhyana/${bookId}?page=${note.page_number}`);
  };

  // A note pinned from a video carries video_id + timestamp_sec in metadata.
  const videoId = note.note_metadata?.video_id as string | undefined;
  const videoSec = note.note_metadata?.timestamp_sec as number | undefined;
  const isVideoNote = note.note_metadata?.source === 'video' && !!videoId && typeof videoSec === 'number';
  const fmtTime = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

  // Jump to the lecture at the noted moment. fromNote flips the watch page's
  // top-left button to "Back to note" (navigate(-1) returns right here).
  const handleWatchAtMoment = () => {
    navigate(`/watch/${videoId}?t=${videoSec}`, { state: { fromNote: true } });
  };

  // Inline [m:ss] seek-pills inside a video note's content (the "In this lecture"
  // section) → open the lecture at that moment. Undefined for non-video notes
  // (book notes have no timestamp pills), so the markdown leaves them inert.
  const onTimestampClick = videoId
    ? (sec: number) => navigate(`/watch/${videoId}?t=${sec}`, { state: { fromNote: true } })
    : undefined;

  // Handle re-index
  const handleReindex = async () => {
    if (!onReindex) return;

    setIsReindexing(true);
    try {
      await onReindex(note.id);
      showToast('success', 'Re-indexing started');
    } catch (err: any) {
      showToast('error', err.message || 'Failed to start re-indexing');
    } finally {
      setIsReindexing(false);
    }
  };

  // Highlight search query in text
  const highlightText = (text: string) => {
    if (!searchQuery.trim()) return text;

    const regex = new RegExp(`(${searchQuery.trim()})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part, i) =>
      regex.test(part) ? (
        <mark key={i} className="bg-cyan-200 dark:bg-cyan-800/50 px-0.5 rounded">
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  return (
    <div
      className="relative group border border-gray-200 dark:border-slate-700/50 rounded-xl bg-white dark:bg-slate-900/50 p-6 hover:border-gray-300 dark:hover:border-slate-600 transition-colors"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-gray-600 dark:text-slate-400">
              {noteTypeLabel}
            </span>
            {isVideoNote ? (
              <>
                <span className="text-gray-300 dark:text-slate-700">·</span>
                <span className="inline-flex items-center gap-1 text-xs font-mono text-cyan-600 dark:text-cyan-400">
                  <PlayCircleIcon className="h-3.5 w-3.5" />Video {fmtTime(videoSec as number)}
                </span>
              </>
            ) : note.page_number > 0 && (
              <>
                <span className="text-gray-300 dark:text-slate-700">·</span>
                <span className="text-xs font-mono text-gray-600 dark:text-slate-400">
                  Page {note.page_number}
                </span>
              </>
            )}
            {getIndexingStatusDot()}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-0.5">
          {/* Re-index button (only shown when indexing failed) */}
          {note.indexing_status === 'failed' && onReindex && (
            <button
              onClick={handleReindex}
              disabled={isReindexing}
              className="p-1.5 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/30 text-amber-600 dark:text-amber-400 transition-colors disabled:opacity-50"
              title="Retry indexing"
            >
              <ArrowPathIcon className={`w-4 h-4 ${isReindexing ? 'animate-spin' : ''}`} />
            </button>
          )}
          {isVideoNote ? (
            <button
              onClick={handleWatchAtMoment}
              className="p-1.5 rounded-lg hover:bg-cyan-100 dark:hover:bg-[#06B6D4]/10 text-cyan-600 dark:text-[#06B6D4] transition-colors"
              title={`Watch at ${fmtTime(videoSec as number)}`}
            >
              <PlayCircleIcon className="w-4 h-4" />
            </button>
          ) : note.page_number > 0 && (
            <button
              onClick={handleJumpToPage}
              className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800/50 text-gray-500 dark:text-slate-400 transition-colors"
              title="Jump to page"
            >
              <BookOpenIcon className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={handleCopy}
            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800/50 text-gray-500 dark:text-slate-400 transition-colors"
            title="Copy to clipboard"
          >
            <ClipboardDocumentIcon className="w-4 h-4" />
          </button>
          <button
            onClick={() => setIsEditing(!isEditing)}
            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800/50 text-gray-500 dark:text-slate-400 transition-colors"
            title="Edit note"
          >
            <PencilIcon className="w-4 h-4" />
          </button>
          <button
            onClick={handleDelete}
            disabled={isDeleting}
            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800/50 text-gray-500 dark:text-slate-400 transition-colors disabled:opacity-50"
            title="Delete note"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Content */}
      {note.note_type === 'chat_answer' && note.normalized_content ? (
        <div className="prose dark:prose-invert max-w-none mb-4 text-gray-900 dark:text-slate-100">
          <MarkdownRenderer content={note.normalized_content} onTimestampClick={onTimestampClick} />
        </div>
      ) : note.content_text ? (
        <div className="prose dark:prose-invert max-w-none mb-4 text-gray-900 dark:text-slate-100">
          <MarkdownRenderer content={note.content_text} onTimestampClick={onTimestampClick} />
        </div>
      ) : null}

      {/* Images */}
      {note.image_urls && note.image_urls.length > 0 && (
        <NoteImageGallery imageUrls={note.image_urls} className="mb-4" />
      )}

      {/* Attachment Context - AI description of question images */}
      {/* Skip for chat_answer notes since it's already embedded in normalized_content */}
      {note.attachment_context && note.note_type !== 'chat_answer' && (
        <div className="border-l-2 border-cyan-400 dark:border-cyan-500 pl-3 mb-4 bg-cyan-50/50 dark:bg-cyan-900/20 py-2 rounded-r">
          <div className="text-xs font-medium text-cyan-600 dark:text-cyan-400 mb-1">Question Context</div>
          <div className="prose dark:prose-invert max-w-none text-sm prose-p:my-1 prose-headings:mt-2 prose-headings:mb-1 prose-ul:my-1 prose-li:my-0.5">
            <MarkdownRenderer content={note.attachment_context} />
          </div>
        </div>
      )}

      {/* User Note */}
      {!isEditing && note.user_note && (
        <div className="border-l-2 border-gray-300 dark:border-slate-600 pl-3 mb-3">
          <div className="text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">Your note</div>
          <div className="text-sm text-gray-700 dark:text-slate-300 whitespace-pre-wrap">
            {highlightText(note.user_note)}
          </div>
        </div>
      )}

      {/* Edit User Note */}
      {isEditing && (
        <div ref={editContainerRef} className="mb-3">
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
            Edit your note
          </label>
          <textarea
            autoFocus
            value={editedNote}
            onChange={(e) => setEditedNote(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100"
            rows={3}
            placeholder="Add your thoughts..."
          />
          <div className="flex gap-2 mt-2">
            <button
              onClick={handleSaveEdit}
              className="px-3 py-1.5 text-sm text-white rounded-lg transition-colors"
              style={{ background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)' }}
            >
              Save
            </button>
            <button
              onClick={() => {
                setIsEditing(false);
                setEditedNote(note.user_note || '');
              }}
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-800/50 text-gray-700 dark:text-slate-300 rounded-lg transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Concept Tags */}
      {((note.concept_tags && note.concept_tags.length > 0) || (note.extracted_concept_tags && note.extracted_concept_tags.length > 0)) && (
        <div className="flex flex-wrap gap-1.5 text-xs">
          {note.concept_tags && note.concept_tags.map((tag, index) => (
            <span
              key={`user-${index}`}
              className="px-2 py-0.5 rounded-lg bg-gray-100 dark:bg-slate-800/50 text-gray-700 dark:text-slate-300 border border-gray-200 dark:border-slate-700"
              title="Your tag"
            >
              {highlightText(tag)}
            </span>
          ))}
          {note.extracted_concept_tags && note.extracted_concept_tags.map((tag, index) => (
            <span
              key={`ai-${index}`}
              className="px-2 py-0.5 rounded-lg bg-gray-100 dark:bg-slate-800/50 text-gray-600 dark:text-slate-400 border border-gray-200 dark:border-slate-700 italic"
              title="AI-extracted concept"
            >
              {highlightText(tag)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
